import styled from '@emotion/styled';
import React from 'react';

import { getColorValue, getRadius, getTextSize, u } from '../../utilities';
import Spacer from '../spacer/spacer';
import Text from '../text/text';

const sizesStyles = {
  small: {
    padding: `${u(1.5)} ${u(1.75)}`,
    textSize: 'small',
  },
  medium: {
    padding: `${u(1.5)} var(--lns-formFieldHorizontalPadding)`,
    textSize: 'medium',
  },
};

const TextareaWrapper = styled.textarea<TextareaProps>`
  width: 100%;
  border: none;
  font-family: inherit;
  color: inherit;
  background-color: ${getColorValue('formFieldBackground')};
  transition: 0.3s box-shadow;
  padding: ${props => sizesStyles[props.size].padding};
  ${getRadius('250')};
  box-shadow: inset 0 0 0 var(--lns-formFieldBorderWidth)
    ${props =>
      props.error
        ? 'var(--lns-color-danger)'
        : 'var(--lns-color-formFieldBorder)'};
  ${props => getTextSize(sizesStyles[props.size].textSize)};
  resize: ${props => props.resize};

  &:hover {
    box-shadow: inset 0 0 0 var(--lns-formFieldBorderWidthFocus)
      ${props =>
        props.error ? 'var(--lns-color-danger)' : 'var(--lns-color-blurple)'};
  }

  &:focus {
    outline: 1px solid
      ${props => (props.error ? 'var(--lns-color-orangeLight)' : 'transparent')};
    box-shadow: ${props =>
      props.error
        ? 'var(--lns-formFieldBorderShadowError)'
        : 'var(--lns-formFieldBorderShadowFocus)'};
  }

  &:disabled {
    color: ${getColorValue('disabledContent')};
    background-color: ${getColorValue('disabledBackground')};
  }

  &:disabled:hover {
    box-shadow: inset 0 0 0 var(--lns-formFieldBorderWidth)
      var(--lns-color-formFieldBorder);
  }

  &::placeholder {
    color: ${getColorValue('bodyDimmed')};
  }
`;

const Textarea = React.forwardRef(
  (
    {
      onChange,
      value,
      rows = 4,
      isDisabled,
      placeholder,
      size = 'medium',
      resize = 'both',
      error = null,
      ...props
    }: TextareaProps & React.ComponentProps<typeof TextareaWrapper>,
    ref: React.ForwardedRef<HTMLTextAreaElement>
  ) => (
    <>
      <TextareaWrapper
        disabled={isDisabled}
        onChange={onChange}
        placeholder={placeholder}
        ref={ref}
        rows={rows}
        value={value}
        size={size}
        resize={resize}
        error={error}
        {...props}
      />
      {error ? (
        <>
          <Spacer bottom="xmsall" />
          <Text color="danger" fontWeight="regular" size="body-md">
            {error}
          </Text>
        </>
      ) : null}
    </>
  )
);

type TextareaProps = {
  rows?: number;
  value?: string;
  placeholder?: string;
  isDisabled?: boolean;
  onChange?: React.ReactEventHandler;
  size?: 'small' | 'medium';
  resize?: 'none' | 'both' | 'horizontal' | 'vertical';
  error?: string;
};

export default Textarea;
